/* eslint-disable local-rules/no-direct-import */
/* eslint-disable camelcase */
import React, { useEffect, useRef, useState } from 'react';

import {
  FocusableItem,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
} from '@szhsin/react-menu';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import {
  getAccountToken,
  getSubAccounts,
  logout,
  logoutFromSubAccount,
  switchToMainAccount,
} from 'actions/user';
import { postMessageToMobileApp } from 'components/app/helpers';
import { ClearCache, RaiseSupportTicket } from 'components/customicons';
import { ProfileMenuMarketingBanner } from 'components/MarketingBanner/ProfileMenu';
import { scalpingRebate } from 'components/placeorder/partials/ScalpingLink/scalping.store';
import Render from 'components/render';
import {
  IS_DEMO,
  IS_INDIAN_EXCHANGE,
  IS_RN_HOME_SCREEN,
  IS_RN_WEBVIEW,
  LANDING_PAGE_URL,
  MARGIN_TYPES,
  SUPPORT_TICKET_URL,
} from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS, STORAGE_KEYS } from 'constants/enums';
import { MIXPANEL_EVENT_NAMES, MIXPANEL_SOURCES } from 'constants/mixpanelEventNames';
import routes from 'constants/routes';
import {
  MIXPANEL_SESSION_REPLAY_CLASS,
  refreshCacheAndReload,
  trackMixpanelEvent,
} from 'helpers/mixpanel-init';
import { filter } from 'helpers/ramda';
import {
  clearWorkboxCache,
  getLoggedUserType,
  getUserAccountNameById,
  hideEmailAddress,
  refreshPage,
} from 'helpers/utils';
import useDidUpdateEffect from 'hooks/useDidUpdateEffect';
import useLocalStorage from 'hooks/useLocalStorage';
import useLockedBody from 'hooks/useLockedBody';
import { useOAuthLogin } from 'hooks/useOAuthLogin';
import useWindowSize from 'hooks/useWindowSize';
import { userMarginModeSelector, userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import {
  Bank,
  LeftArrowIcon,
  // JewelIcon,
  LogoutIcon,
  // TickIcon,
  // RaiseSupportTicket,
  MenuIdentityVerificationIcon,
  PNLAnalyticsLight,
  PreferencesIcon,
  Profile,
  RightArrowIcon,
  SelectedTag,
  SubAccount,
} from 'styles/assets/icons';
import { LoggedUserType } from 'types/IUser';
import Box from 'UIKit/Box';

import KycStatusButton from '../kycStatusButton/KycStatusButton';
import {
  ApiKeysIcon,
  PortfolioMarginIcon,
  ReferAndEarnIcon,
  SecurityIcon,
  TrnLogsIcon,
} from './icons';
import styles from './index.module.scss';

// created below component in order to use useLockedBody hook
const CustomMenuButton = React.forwardRef(({ open, onClick }, ref) => {
  const buttonRef = useRef(null);

  useEffect(() => {
    if (ref) {
      // eslint-disable-next-line no-param-reassign
      ref.current = buttonRef.current;
    }
  }, [ref]);

  useLockedBody(open);

  return (
    <MenuButton
      ref={buttonRef}
      data-testid="switchUserMenuButton"
      className={`${open ? styles.active : ''} ${styles.button}`}
      id="profile-menu-button"
      onClick={onClick}
    >
      <Profile className={styles.profileIcon} />
    </MenuButton>
  );
});

CustomMenuButton.propTypes = {
  open: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

export const getUserActions = (isScreenMd, userMarginMode = '') => [
  {
    id: 0,
    label: 'Sub Accounts',
    link: '/app/account/subaccounts',
    icon: <SubAccount className={styles['sub-account-icon']} />,
    target: '_self',
    rel: '',
    key: 'subAccount',
    isExternal: false,
    isShown: true,
    hideOnMobile: false,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    link: `/app${routes.account.preferences}`,
    icon: <PreferencesIcon className={styles[`left-icon`]} />,
    target: '_self',
    rel: '',
    key: 'preferences',
    isExternal: false,
    hideOnMobile: true,
    isShown: IS_INDIAN_EXCHANGE,
  },
  {
    id: 1,
    label: 'Portfolio Margin',
    link: `/app${routes.account.portfolioMargin}`,
    icon: (
      <PortfolioMarginIcon
        className={`${styles[`portfolio-icon`]} ${styles[`left-icon`]}`}
      />
    ),
    target: '_self',
    rel: '',
    key: 'portfolio',
    isExternal: false,
    isShown:
      !IS_INDIAN_EXCHANGE &&
      userMarginMode !== MARGIN_TYPES.ISOLATED &&
      userMarginMode !== MARGIN_TYPES.CROSS,
    hideOnMobile: false,
  },
  // {
  //   id: 2,
  //   label: 'Positions',
  //   link: `/app/account/positions`,
  //   icon: (
  //     <PositionsIcon
  //       color={'var(--main-text-primary)'}
  //       className={`${styles[`positions-icon`]} ${styles[`left-icon`]}`}
  //     />
  //   ),
  //   target: '_self',
  //   rel: '',
  //   key: 'positions',
  //   isExternal: false,
  //   hideOnMobile: true,
  // },
  {
    id: 10,
    label: 'Account Analytics',
    link: `/app${routes.account.accountAnalytics.default}`,
    icon: (
      <PNLAnalyticsLight
        className={cn(styles[`left-icon`], styles.accountAnalyticsIcon)}
      />
    ),
    target: '_self',
    rel: '',
    key: 'pnlAnalytics',
    isExternal: false,
    isShown: true,
  },
  // {
  //   id: 11,
  //   link: `/app${routes.optionsLeaderboard}`,
  //   label: 'Options LeaderBoard',
  //   icon: <CrownIcon className={cn(styles[`left-icon`], styles.leaderBoardIcon)} />,
  //   key: 'optionsLeaderBoard',
  //   rel: '',
  //   isExternal: false,
  //   isShown: true,
  //   tag: <NewPill />,
  // },
  {
    id: 'bank_details',
    label: 'Bank Details',
    key: 'bankDetails',
    isShown: IS_INDIAN_EXCHANGE && !IS_DEMO,
    link: `/app${routes.account.bankDetails}`,
    icon: (
      <Bank
        className={cn(styles[`left-icon`], styles.bankDetailsIcon)}
        width={18}
        height={18}
      />
    ),
  },
  {
    id: 3,
    label: 'Refer & Earn',
    link: '/referral-program',
    icon: <ReferAndEarnIcon className={styles[`left-icon`]} />,
    target: '_self',
    rel: '',
    key: 'referEarn',
    isExternal: true,
    isShown: !IS_DEMO,
    hideOnMobile: false,
  },
  {
    id: 4,
    label: 'Security',
    // link: routes.account.security.default,
    link: '/app/account/security',
    icon: <SecurityIcon className={styles[`left-icon`]} />,
    target: '_self',
    rel: '',
    key: 'security',
    isExternal: false,
    isShown: true,
    hideOnMobile: false,
  },
  {
    id: 5,
    label: 'API Keys',
    // link: routes.account.manageapikeys,
    link: '/app/account/manageapikeys',
    icon: <ApiKeysIcon className={styles[`left-icon`]} />,
    target: '_self',
    rel: '',
    key: 'apiKeys',
    isExternal: false,
    isShown: true,
    hideOnMobile: false,
  },
  {
    id: 6,
    label: 'Transaction Logs',
    link: '/app/account/transaction_logs/asset_history',
    // link: routes.account.transactionLog.asset_history,
    icon: <TrnLogsIcon className={styles[`left-icon`]} />,
    target: '_self',
    rel: '',
    key: 'transactionLogs',
    isExternal: false,
    isShown: true,
    hideOnMobile: false,
  },
  {
    id: 7,
    label: 'Raise Support Ticket',
    // link: `https://delta.exchange/support-ticket`,
    link: SUPPORT_TICKET_URL,
    icon: <RaiseSupportTicket className={styles['left-icon']} />,
    target: '_blank',
    rel: 'noopener noreferrer',
    key: 'raiseSupportTicket',
    isExternal: true,
    hideOnMobile: false,
    isShown: isScreenMd && !IS_INDIAN_EXCHANGE,
  },
  // {
  //   id: 8,
  //   label: 'Feedback',
  //   link: `${LANDING_PAGE_URL}/feedback-form`,
  //   icon: <FeedBackIcon className={styles[`left-icon`]} />,
  //   target: '_self',
  //   rel: '',
  //   key: 'feedback',
  //   isExternal: true,
  //   hideOnMobile: false,
  //   isShown: isMobileOrTablet(),
  // },
];

const ProfileMenu = () => {
  const { t } = useTranslation(['headerFooter', 'account']);
  const dispatch = useDispatch();
  const history = useHistory();
  const { setOAuthLoginState } = useOAuthLogin();
  const [prevUser, setPrevUser] = useState({});
  const user = useSelector(state => userSelector(state));
  const userMarginMode = useAppSelector(userMarginModeSelector);
  const { is_kyc_done, email, id } = user;
  const sub_accounts = user.main_account?.sub_accounts || user.sub_accounts;
  // const tradePreferences = useSelector(state => state.tradePreference);
  const [isSwitchUserMenu, setIsSwitchUserMenu] = useState(false);
  const accountName = getUserAccountNameById(id, user);
  const loggedUserType = getLoggedUserType(user);
  const { isScreenMd } = useWindowSize();
  const [, setReset2faPopupShown] = useLocalStorage(
    STORAGE_KEYS.RESET_2FA_POPUP_SHOWN_KEY,
    false
  );

  const EXCLUDE_PAGE_RELOAD = ['positions'];

  const userMenuList =
    loggedUserType === LoggedUserType.SUB_ACCOUNT_LOGIN
      ? filter(
          data => data.key !== 'subAccount',
          getUserActions(isScreenMd, userMarginMode)
        )
      : getUserActions(isScreenMd, userMarginMode);
  const getLocalizedText = localeKey => {
    const str = `headerFooter:header.profile.${localeKey}`;
    return t(str);
  };

  useDidUpdateEffect(() => {
    const timer = setTimeout(() => {
      if (prevUser.main_account) {
        logoutFromSubAccount(prevUser.token).then(() => {
          setPrevUser({});
          refreshPage();
        });
      } else {
        refreshPage();
      }
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [user.id]);

  const switchAccount = (user_id, sub_user) => {
    if (user_id !== user.id) {
      if (IS_RN_HOME_SCREEN()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
      }
      setPrevUser({
        token: user.token,
        main_account: user.main_account,
      });
      dispatch(
        getAccountToken({
          sub_account_user_id: user_id,
        })
      );
    }
    const destinationAccount = getUserAccountNameById(user_id, sub_user);
    trackMixpanelEvent('Account Switched - All Pages - Subaccounts', {
      source: 'header',
      source_account_name: accountName,
      destination_account_name: destinationAccount,
    });
  };

  const switchToMainAcc = () => {
    if (user.main_account) {
      if (IS_RN_HOME_SCREEN()) {
        postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT);
      }
      setPrevUser({
        token: user.token,
        main_account: user.main_account,
      });
      dispatch(switchToMainAccount());
    }
    trackMixpanelEvent('Account Switched - All Pages - Subaccounts', {
      source: 'header',
      source_account_name: accountName,
      destination_account_name: 'Main',
    });
  };
  const onLogoutClick = () => {
    dispatch(logout()).then(() => {
      clearWorkboxCache();
      scalpingRebate.resetRebateAmount();
      if (IS_RN_WEBVIEW) {
        setOAuthLoginState();
      }
      setReset2faPopupShown(false);
    });

    history.push(routes.webAppHome);
  };

  const onItemClick = item => {
    switch (item.key) {
      case 'subAccount':
        trackMixpanelEvent('Accessed - All Pages - Subaccounts', {
          source: 'Header',
        });
        break;
      case 'portfolio':
        trackMixpanelEvent('Portfolio Details Checked', {
          source: 'Header',
        });
        break;
      case 'transactionLogs':
        trackMixpanelEvent('Transaction Logs Clicked - All Pages');
        trackMixpanelEvent('Asset History Accessed - Account - Transaction Logs');
        break;
      case 'positions':
        trackMixpanelEvent('Positions Clicked - All Pages');
        trackMixpanelEvent('Positions Accessed - Account');
        history.push(routes.account.positions);
        break;
      case 'accountAnalytics':
        trackMixpanelEvent('PNL_Analytics_Opened', {
          page_name: 'Profile Dropdown',
        });
        break;
      case 'raiseSupportTicket':
        trackMixpanelEvent('Raise Support Ticket link clicked', {
          source: 'Account Menu',
          page_name: `${LANDING_PAGE_URL}${window.location.pathname}`,
        });
        break;
      case 'preferences':
        trackMixpanelEvent(MIXPANEL_EVENT_NAMES.PREFERENCES_CLICKED, {
          source: MIXPANEL_SOURCES.PROFILE,
        });
        break;
      default:
        trackMixpanelEvent(`${item.label} Clicked - All Pages`);
    }
  };

  const onProfileClick = () => {
    trackMixpanelEvent('Profile Clicked - All Pages');
    history.push(routes.account.profile);
  };

  const onKycStatusClick = () => {
    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.ACCOUNT.IDENTITY_VERIFICATION, {
      source: 'Profile',
    });
    history.push(routes.account.identityVerification);
  };

  useEffect(() => {
    dispatch(getSubAccounts());
  }, [dispatch]);

  if (isSwitchUserMenu) {
    return (
      <Menu
        menuButton={({ open }) => (
          <CustomMenuButton
            data-testid="customSwitchUserMenuButton"
            open={open}
            onClick={() => {
              trackMixpanelEvent('Account icon clicked - All Pages - Header');
            }}
          />
        )}
        menuClassName={styles['menu-container']}
      >
        <MenuItem
          data-testid="closeSwitchUserMenuButton"
          className={styles[`switch-account`]}
          onClick={e => {
            setIsSwitchUserMenu(false);
            // eslint-disable-next-line no-param-reassign
            e.keepOpen = true;
          }}
          href={() => false}
        >
          <div>
            <LeftArrowIcon className={styles['right-arrow']} />{' '}
            <span className={styles['user-title']}>
              {t(`headerFooter:header.sideMenu.switchSubAccounts`)}
            </span>
          </div>
        </MenuItem>
        <MenuDivider className={styles.divider} />
        <Box className={styles.scrollItems}>
          <MenuItem
            data-testid="mainAccountOption-switchUserMenu"
            className={` ${styles['switch-user-menu-item']}`}
            onClick={switchToMainAcc}
          >
            <div className={`${!user.main_account ? styles.active : ''}`}>
              Main Account
            </div>
            {!user.main_account && <SelectedTag className={styles['active-icon']} />}
          </MenuItem>

          {/* <MenuDivider className={styles.divider} /> */}

          {sub_accounts.map((subUser, i) => {
            return (
              <MenuItem
                data-testid={`subAccount${i + 1}-switchUserMenu`}
                key={subUser.id}
                className={` ${styles['switch-user-menu-item']}`}
                onClick={() => switchAccount(subUser.id, subUser)}
              >
                <div className={`${user.id === subUser.id ? styles.active : ''}`}>
                  {subUser.account_name}
                </div>
                {user.id === subUser.id && (
                  <SelectedTag className={styles['active-icon']} />
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    );
  }

  return (
    (<Menu
      menuButton={({ open }) => (
        <CustomMenuButton
          data-testid="customProfileMenuButton"
          open={open}
          onClick={() => {
            trackMixpanelEvent('Account icon clicked - All Pages - Header');
          }}
        />
      )}
      menuClassName={styles['menu-container']}
    >
      <FocusableItem className={styles['user-details']}>
        <Box
          as="div"
          data-testid="profileLink-profileMenu"
          className={cn('w-100', styles.id)}
          onClick={onProfileClick}
        >
          <div>
            <span className={styles['email-id']}>{hideEmailAddress(email)}</span>{' '}
            <RightArrowIcon className={styles['right-arrow']} />
          </div>

          <div className={styles['user-vip-level']}>
            <span className={cn(styles['user-id'], MIXPANEL_SESSION_REPLAY_CLASS)}>
              {getLocalizedText('userId')} : {id}
            </span>
          </div>
          <div
            className={cn(
              'flex-set flex--align-items-center flex--content-start',
              styles['user-info-level']
            )}
          >
            <span className={styles['account-label']}>Account</span>
            <div className={cn('truncate', styles['account-info'])}>{accountName}</div>
            {loggedUserType !== LoggedUserType.SUB_ACCOUNT_LOGIN &&
              sub_accounts.length > 0 && (
                <Box
                  as="span"
                  data-testid="subAccountSwitchButton-profileMenu"
                  className={cn(
                    'flex-set flex--align-items-center flex--content-center',
                    styles['switch-span'],
                    {
                      [styles['switch-spanIndianExchange']]: IS_INDIAN_EXCHANGE,
                    }
                  )}
                  onClick={e => {
                    setIsSwitchUserMenu(true);
                    e.stopPropagation();
                  }}
                >
                  Switch <RightArrowIcon className={styles['right-arrow']} />
                </Box>
              )}
          </div>
        </Box>

        <Render when={!IS_DEMO}>
          <KycStatusButton />
        </Render>
      </FocusableItem>
      {/* <MenuItem className={styles[`user-status`]}>
        <a
          data-testid="vipProgramLink-profileMenu"
          href="https://www.delta.exchange/blog/support/vip-program"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div>
            <span className={styles['vip-level']}>
              <JewelIcon width="10" height="8" /> {getLocalizedText('vip')}:{' '}
              {tradePreferences.vip_level}
            </span>
            <span className={styles['discount-level']}>
              {getLocalizedText('getFeeDiscount')}
              <RightArrowIcon className={styles['right-arrow']} />
            </span>
          </div>
        </a>
      </MenuItem> */}
      {!is_kyc_done && !IS_DEMO&& (
        <MenuItem
          data-testid="identityVerificationOption-profileMenu"
          className={styles[`profile-menu-item`]}
          onClick={onKycStatusClick}
        >
          <MenuIdentityVerificationIcon
            className={`${styles[`identity-verification-icon`]} ${styles[`left-icon`]}`}
          />{' '}
          {getLocalizedText('identityVerification')}{' '}
          <RightArrowIcon className={styles['right-arrow']} />
        </MenuItem>
      )}
      {/* <MenuItem
        className={styles[`profile-menu-item`]}
        onClick={e => {
          setIsSwitchUserMenu(true);
          e.keepOpen = true;
        }}>
        <SubAccount className={styles['sub-account-icon']} />{' '}
        {t(`headerFooter:header.sideMenu.subAccounts`)}
      </MenuItem> */}
      {userMenuList.map(
        item =>
          item.isShown && (
            <MenuItem
              key={item.id}
              className={cn(styles[`profile-menu-item`], styles[item.key])}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(EXCLUDE_PAGE_RELOAD.includes(item.key) ? {} : { href: item.link })}
              // href={item.link}
              onClick={() => {
                onItemClick(item);
                // label
              }}
              data-testid={`${item.key}Option-profileMenu`}
              target={item.target}
              rel={item.rel}
            >
              {item.icon}
              <span>{getLocalizedText(item.key)} </span>
              {item.tag && <span data-palette="ProfileMenu">{item.tag}</span>}
              <RightArrowIcon className={styles['right-arrow']} />
            </MenuItem>
          )
      )}
      <MenuItem
        data-testid="clearCacheOption-profileMenu"
        className={styles[`profile-menu-item`]}
        onClick={() => refreshCacheAndReload('profile Menu')}
      >
        <ClearCache className={styles['clear-cache-icon']} />{' '}
        {t(`headerFooter:header.sideMenu.clearCache`)}
      </MenuItem>
      <MenuItem
        data-testid="logoutOption-profileMenu"
        className={styles[`profile-menu-item`]}
        onClick={onLogoutClick}
        id="logout-button"
      >
        <LogoutIcon className={styles['logout-icon']} /> {getLocalizedText('logout')}
      </MenuItem>
      <MenuItem className={cn(styles['profile-menu-item'], styles['marketing-banner'])}>
        <ProfileMenuMarketingBanner />
      </MenuItem>
    </Menu>)
  );
};

export default ProfileMenu;
